import { useState, useEffect } from 'react';
import { Mail, X, Zap, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : `${window.location.origin}/api`;

const SEEN_KEY = 'sk_scores_email_capture_v1';

/**
 * Live Scores email capture — a one-time, skippable popup shown shortly
 * after first launch. Saves the email to the iOS waitlist (source
 * "scores_app") so the user receives the Monday weekly free-tips digest
 * that funnels them to the AI Predictions app. Never shown again once the
 * user submits or dismisses it, nor to users who are already logged in.
 */
const ScoresEmailCapture = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    try {
      if (localStorage.getItem(SEEN_KEY)) return;       // already seen/dismissed
      if (localStorage.getItem('token')) return;        // already a SmartKick user
    } catch { /* ignore */ }
    // Don't pop over interactive detail pages (team profile / match detail) —
    // it would sit on top of the squad list, player modal & odds and block taps.
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (path.includes('/team/') || path.includes('/match/')) return;
    const t = setTimeout(() => setOpen(true), 5000);    // shortly after first launch
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    try { localStorage.setItem(SEEN_KEY, new Date().toISOString()); } catch { /* ignore */ }
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value.includes('@') || !value.includes('.')) {
      setError('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const resp = await fetch(`${API_URL}/waitlist/ios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value, source: 'scores_app' }),
      });
      if (!resp.ok) throw new Error('signup failed');
      try { localStorage.setItem(SEEN_KEY, new Date().toISOString()); } catch { /* ignore */ }
      setDone(true);
      setTimeout(() => setOpen(false), 1800);
    } catch {
      setError('Something went wrong — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="scores-email-capture"
    >
      <div className="bg-[#0e1525] border border-emerald-500/25 rounded-2xl max-w-md w-full p-6 relative">
        <button
          onClick={close}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10"
          aria-label="Close"
          data-testid="scores-email-close"
        >
          <X className="w-4 h-4 text-zinc-500" />
        </button>

        {done ? (
          <div className="text-center py-6" data-testid="scores-email-success">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
              <Check className="w-6 h-6 text-emerald-400" />
            </div>
            <h3 className="text-lg font-black text-white">You're in! 🎯</h3>
            <p className="text-sm text-zinc-400 mt-1">Free tips land in your inbox every Monday. Check your email for confirmation.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
              </div>
              <span className="text-base font-black text-white">SmartKick</span>
            </div>
            <h3 className="text-xl font-black text-white mt-3 leading-tight">
              Get free AI football tips every week
            </h3>
            <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">
              Drop your email and we'll send you our best free picks every Monday — plus a <span className="text-emerald-400 font-semibold">14-day free VIP trial</span> of the full AI Predictions app.
            </p>

            <form onSubmit={submit} className="mt-5 space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="you@email.com"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/50"
                  data-testid="scores-email-input"
                  autoFocus
                />
              </div>
              {error && <p className="text-xs text-red-400" data-testid="scores-email-error">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm transition-colors disabled:opacity-50"
                data-testid="scores-email-submit"
              >
                {submitting ? 'Saving…' : 'Get free weekly tips'}
              </button>

              <button
                type="button"
                onClick={close}
                className="w-full py-2 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                data-testid="scores-email-skip"
              >
                Maybe later
              </button>
            </form>

            <p className="text-[10px] text-zinc-600 mt-4 text-center leading-relaxed">
              18+ · Gamble responsibly · begambleaware.org · Unsubscribe anytime.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ScoresEmailCapture;
